import {weakMemo} from '@packages/utilities/memo';
import {isQuickEffect} from '@packages/systems/ix2/shared-utils';
import {
  ActionTypeConsts,
  QuickEffectIds,
} from '@packages/systems/ix2/shared-constants';
import {
  ActionItemType,
  ActionListsType,
  ActionListType,
  EventsType,
  PluginLottieActionType,
} from '@packages/systems/ix2/types-core';

const IX2_ID_DELIMITER = '|';

const hasPageId = (id: string) => id && id.includes(IX2_ID_DELIMITER);

// @ts-expect-error - TS7023 - 'getNodeIdFromIx2Id' implicitly has return type 'any' because it does not have a return type annotation and is referenced directly or indirectly in one of its return expressions.
const getNodeIdFromIx2Id = (targetOrId) => {
  if (targetOrId && typeof targetOrId === 'object') {
    return getNodeIdFromIx2Id(targetOrId.id || targetOrId.nodeId);
  }

  return hasPageId(targetOrId)
    ? targetOrId.split(IX2_ID_DELIMITER)[1]
    : targetOrId;
};

const getPageIdFromIx2Id = (targetId: string) =>
  hasPageId(targetId) ? targetId.split(IX2_ID_DELIMITER)[0] : targetId;

export type Ix2NodeMap = {
  nodeIdSet: Set<string>;
  styleBlockIdsSet: Set<string>;
  pageIdSet: Set<string>;
  pluginInitialStateValues: {
    [key: string]: {
      value: number;
    };
  };
  pluginAnimationIdSet: {
    nodeIds: Set<string>;
    styleBlockIds: Set<string>;
  };
};

const toActionListMap = weakMemo((actionLists: ActionListsType) =>
  actionLists.reduce<Record<ActionListType['id'], ActionListType>>(
    (result, actionList) => {
      result[actionList.id] = actionList;
      return result;
    },
    {}
  )
);

export const getIx2NodeMap = weakMemo((events: EventsType) =>
  weakMemo((actionLists: ActionListsType): Ix2NodeMap => {
    const rawActionListsMap = toActionListMap(actionLists);

    const {
      nodeIdSet,
      styleBlockIdsSet,
      pageIdSet,
      pluginInitialStateValues,
      pluginAnimationIdSet,
    } = events.reduce(
      (result, event) => {
        if (event == null || event.mediaQueries.length === 0) return result; // Event won't trigger since all queries are off

        const targets =
          Array.isArray(event.targets) && event.targets.length > 0
            ? event.targets
            : [event.target];

        targets.forEach((target) => {
          if (target == null) return;

          const {appliesTo, id: targetId, styleBlockIds} = target;
          const eventAction = event.action;
          if (eventAction == null) return;

          const actionListIdForEvent = eventAction.config?.actionListId;
          const actionList = actionListIdForEvent
            ? rawActionListsMap[actionListIdForEvent]
            : null;

          if (isQuickEffect(eventAction.actionTypeId) || actionList) {
            const nodeId = getNodeIdFromIx2Id(targetId);

            if (appliesTo === 'CLASS') {
              result.styleBlockIdsSet.add(styleBlockIds.join('|'));
            } else if (appliesTo === 'ELEMENT') {
              result.nodeIdSet.add(nodeId);

              const pageId =
                targetId && hasPageId(targetId)
                  ? getPageIdFromIx2Id(targetId)
                  : undefined;
              pageId && result.pageIdSet.add(pageId);
            } else if (appliesTo === 'PAGE' && targetId) {
              result.pageIdSet.add(targetId);
            }

            if (
              eventAction.actionTypeId === QuickEffectIds.PLUGIN_LOTTIE_EFFECT
            ) {
              result.pluginAnimationIdSet.nodeIds.add(nodeId);
            }
          }

          // Skip if no action list or actionList already visited
          if (!actionList || result.actionListVisited.has(actionList.id)) {
            return;
          }

          const processActionItem = (
            actionListId: string,
            actionItem: ActionItemType,
            useFirstGroupAsInitialState: boolean,
            groupIndex: number
          ): void => {
            const configTarget = actionItem.config.target;
            if (configTarget == null) return;

            if (
              configTarget.appliesTo === 'ELEMENT_CLASS' &&
              configTarget.styleBlockIds
            ) {
              const id = configTarget.styleBlockIds.join('|');
              result.styleBlockIdsSet.add(id);

              if (actionItem.actionTypeId === ActionTypeConsts.PLUGIN_LOTTIE) {
                result.pluginAnimationIdSet.styleBlockIds.add(id);

                if (useFirstGroupAsInitialState && groupIndex === 0) {
                  result.pluginInitialStateValues[id] = {
                    value: (actionItem as PluginLottieActionType).config.value,
                  };
                }
              }

              return;
            }

            const id = getNodeIdFromIx2Id(configTarget);
            result.nodeIdSet.add(id);

            const pageId =
              configTarget.nodeId && hasPageId(configTarget.nodeId)
                ? getPageIdFromIx2Id(configTarget.nodeId)
                : undefined;
            pageId && result.pageIdSet.add(pageId);

            if (actionItem.actionTypeId === ActionTypeConsts.PLUGIN_LOTTIE) {
              if (configTarget.appliesTo === 'TRIGGER_ELEMENT') {
                const nodeId = getNodeIdFromIx2Id(targetId);
                result.pluginAnimationIdSet.nodeIds.add(nodeId);

                result.actionListVisited.delete(actionListId);
              } else {
                result.pluginAnimationIdSet.nodeIds.add(id);
              }

              if (useFirstGroupAsInitialState && groupIndex === 0) {
                result.pluginInitialStateValues[id] = {
                  value: (actionItem as PluginLottieActionType).config.value,
                };
              }
            }
          };

          result.actionListVisited.add(actionList.id);

          if ('actionItemGroups' in actionList) {
            actionList.actionItemGroups.forEach(({actionItems}, groupIndex) => {
              actionItems.forEach((actionItem) => {
                processActionItem(
                  actionList.id,
                  actionItem,
                  actionList.useFirstGroupAsInitialState,
                  groupIndex
                );
              });
            });
          }

          if ('continuousParameterGroups' in actionList) {
            actionList.continuousParameterGroups.forEach(
              ({continuousActionGroups}) => {
                continuousActionGroups.forEach(({actionItems}, groupIndex) => {
                  actionItems.forEach((actionItem) => {
                    processActionItem(
                      actionList.id,
                      actionItem,
                      false,
                      groupIndex
                    );
                  });
                });
              }
            );
          }
        });

        return result;
      },
      {
        actionListVisited: new Set(),
        nodeIdSet: new Set() as Set<string>,
        pageIdSet: new Set() as Set<string>,
        styleBlockIdsSet: new Set() as Set<string>,
        pluginInitialStateValues: {} as Record<string, {value: number}>,
        pluginAnimationIdSet: {
          nodeIds: new Set(),
          styleBlockIds: new Set(),
        } as {
          nodeIds: Set<string>;
          styleBlockIds: Set<string>;
        },
      }
    );

    return {
      nodeIdSet,
      pageIdSet,
      styleBlockIdsSet,
      pluginInitialStateValues,
      pluginAnimationIdSet,
    };
  })
);

function isClassTargetedByIx2(
  styleBlockIdsSet: Set<string>,
  nodeStyleBlockIds: JoinableStringColl
) {
  return styleBlockIdsSet.has(nodeStyleBlockIds.join('|'));
}

export function isNodeTargetedByIx2Plugin(
  nodeId: string,
  pluginAnimationIdSet: Ix2NodeMap['pluginAnimationIdSet'],
  styleBlockIds: JoinableStringColl
) {
  return (
    pluginAnimationIdSet.nodeIds.has(nodeId) ||
    isClassTargetedByIx2(pluginAnimationIdSet.styleBlockIds, styleBlockIds)
  );
}

interface JoinableStringColl {
  join(separator: string): string;
}
